import {
  BellIcon,
  FlaskConicalIcon,
  LibraryIcon,
  ListTodoIcon,
  PaperclipIcon,
  TrendingUpIcon,
  UserCircleIcon,
  ZapIcon,
} from "lucide-react";
import { NavLink } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import useCurrentUser from "@/hooks/useCurrentUser";
import { useNotifications } from "@/hooks/useUserQueries";
import { cn } from "@/lib/utils";
import { Routes } from "@/router";
import { UserNotification_Status } from "@/types/proto/api/v1/user_service_pb";
import { useTranslate } from "@/utils/i18n";
import JournalsLogo from "./JournalsLogo";
import UserMenu from "./UserMenu";

interface NavLinkItem {
  id: string;
  path: string;
  title: string;
  icon: React.ReactNode;
}

interface Props {
  collapsed?: boolean;
  className?: string;
}

const Navigation = (props: Props) => {
  const { collapsed, className } = props;
  const t = useTranslate();
  const currentUser = useCurrentUser();
  const { data: notifications = [] } = useNotifications();

  // Check if we're in dev mode (show test links on localhost or dev ports)
  const isDevMode =
    typeof window !== "undefined" &&
    (window.location.port === "8081" ||
      window.location.port === "8082" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  const homeNavLink: NavLinkItem = {
    id: "header-memos",
    path: Routes.ROOT,
    title: t("common.memos"),
    icon: <LibraryIcon className="w-6 h-auto shrink-0" />,
  };
  const attachmentsNavLink: NavLinkItem = {
    id: "header-attachments",
    path: Routes.ATTACHMENTS,
    title: t("common.attachments"),
    icon: <PaperclipIcon className="w-6 h-auto shrink-0" />,
  };
  const tickersNavLink: NavLinkItem = {
    id: "header-tickers",
    path: Routes.TICKERS,
    title: "Tickers",
    icon: <TrendingUpIcon className="w-6 h-auto shrink-0" />,
  };
  const actionsNavLink: NavLinkItem = {
    id: "header-actions",
    path: "/actions",
    title: "Actions",
    icon: <ZapIcon className="w-6 h-auto shrink-0" />,
  };
  const checklistTemplatesNavLink: NavLinkItem = {
    id: "header-checklist-templates",
    path: Routes.CHECKLIST_TEMPLATES,
    title: t("editor.checklist-templates"),
    icon: <ListTodoIcon className="w-6 h-auto shrink-0" />,
  };
  const unreadCount = notifications.filter((n) => n.status === UserNotification_Status.UNREAD).length;
  const inboxNavLink: NavLinkItem = {
    id: "header-inbox",
    path: Routes.INBOX,
    title: t("common.inbox"),
    icon: (
      <div className="relative">
        <BellIcon className="w-6 h-auto shrink-0" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-primary text-primary-foreground text-[10px] font-semibold rounded-full border-2 border-background">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </div>
    ),
  };
  const signInNavLink: NavLinkItem = {
    id: "header-auth",
    path: Routes.AUTH,
    title: t("common.sign-in"),
    icon: <UserCircleIcon className="w-6 h-auto shrink-0" />,
  };

  // Dev test links (only in dev mode)
  const devTestLinks: NavLinkItem[] = isDevMode
    ? [
        {
          id: "dev-test-dashboard",
          path: "/test-dashboard",
          title: "Test Dashboard",
          icon: <FlaskConicalIcon className="w-6 h-auto shrink-0" />,
        },
      ]
    : [];

  const navLinks: NavLinkItem[] = currentUser
    ? [homeNavLink, tickersNavLink, actionsNavLink, checklistTemplatesNavLink, attachmentsNavLink, inboxNavLink, ...devTestLinks]
    : [signInNavLink];

  return (
    <header className={cn("w-full h-full overflow-auto flex flex-col justify-between items-start gap-4 hide-scrollbar", className)}>
      <div className="w-full px-1 py-1 flex flex-col justify-start items-start space-y-2 overflow-auto overflow-x-hidden hide-scrollbar shrink">
        <NavLink className="mb-3 cursor-default" to={Routes.ROOT}>
          <JournalsLogo collapsed={collapsed} />
        </NavLink>
        {navLinks.map((navLink) => (
          <NavLink
            className={({ isActive }) =>
              cn(
                "group relative px-2 py-2.5 rounded-2xl border flex flex-row items-center text-lg transition-all duration-300",
                collapsed ? "" : "w-full px-4",
                // Active state with glass-card and amber gradient
                isActive
                  ? "bg-gradient-to-r from-amber-500/10 to-orange-500/10 text-foreground border-amber-500/30 shadow-lg shadow-amber-500/10"
                  : "border-transparent text-sidebar-foreground opacity-80 hover:opacity-100",
                // Hover state for inactive items
                !isActive &&
                  "hover:bg-gradient-to-r hover:from-amber-500/5 hover:to-orange-500/5 hover:border-amber-500/20 hover:shadow-md hover:shadow-amber-500/5",
              )
            }
            key={navLink.id}
            to={navLink.path}
            id={navLink.id}
            viewTransition
          >
            {({ isActive }) => (
              <>
                {/* Glow effect on hover */}
                {!isActive && (
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-500/0 via-amber-500/10 to-orange-500/0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                )}

                {/* Icon with tooltip when collapsed */}
                {props.collapsed ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="relative flex-shrink-0">
                          {/* Icon glow */}
                          {isActive && (
                            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-lg blur-md -z-10" />
                          )}
                          <div className={cn("transition-transform duration-300", isActive && "scale-110")}>{navLink.icon}</div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p>{navLink.title}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <>
                    {/* Icon container */}
                    <div className="relative flex-shrink-0">
                      {/* Icon glow */}
                      {isActive && (
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-lg blur-md -z-10" />
                      )}
                      <div className={cn("transition-transform duration-300", isActive && "scale-110")}>{navLink.icon}</div>
                    </div>

                    {/* Text label */}
                    <span className="ml-3 truncate font-medium transition-all duration-300">{navLink.title}</span>
                  </>
                )}
              </>
            )}
          </NavLink>
        ))}
      </div>

      {/* Bottom section: User menu */}
      <div className={cn("w-full flex flex-col justify-end gap-2", props.collapsed ? "items-center" : "items-start pl-3")}>
        {/* User menu */}
        {currentUser && <UserMenu collapsed={collapsed} />}
      </div>
    </header>
  );
};

export default Navigation;
